import { useQuery, useQueryClient } from '@tanstack/react-query';
import { bonsCommandeAPI } from '../../services/api';
import {
  BonCommande,
  BonCommandeFilters,
  BonCommandeListResponse,
} from '../../types/bonCommande';

/**
 * Query hooks for Bons de Commande (server-paginated list + deleted list).
 *
 * Key root MUST be 'bons-commande' — the slug the realtime-invalidation
 * middleware broadcasts for /api/bons-commande — so another user's change
 * auto-refreshes this list. Invalidating the root key refreshes every
 * paginated/filtered variant and the deleted list at once.
 */
export interface BonsCommandeListParams {
  skip: number;
  limit: number;
  search?: string;
  filters?: BonCommandeFilters;
}

export const bonsCommandeKeys = {
  root: ['bons-commande'] as const,
  list: (params: BonsCommandeListParams) => ['bons-commande', 'list', params] as const,
  deleted: ['bons-commande', 'deleted'] as const,
};

export function useBonsCommandeList(params: BonsCommandeListParams, enabled: boolean) {
  return useQuery<BonCommandeListResponse>({
    queryKey: bonsCommandeKeys.list(params),
    queryFn: () =>
      bonsCommandeAPI.getPaginated(
        params.skip,
        params.limit,
        params.search,
        params.filters,
      ),
    enabled,
    keepPreviousData: true,
  });
}

export function useDeletedBonsCommande(enabled: boolean) {
  return useQuery<BonCommande[]>({
    queryKey: bonsCommandeKeys.deleted,
    queryFn: () => bonsCommandeAPI.getDeleted(),
    enabled,
  });
}

export function useInvalidateBonsCommande() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: bonsCommandeKeys.root });
}
